import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { ShellLayout } from "@/components/shell/ShellLayout";
import { Spinner } from "@/components/common/FullScreenLoader";
import { OutcomeBadge } from "@/components/shell/Badges";
import { EmptyState, SectionHeading } from "@/components/shell/StatCard";
import { api, apiError } from "@/lib/api";
import { formatDateTime, shortId } from "@/lib/format";

const TYPE_FILTERS = [
  { value: "", label: "All events" },
  { value: "auth.login", label: "Sign-ins" },
  { value: "auth.mfa", label: "MFA" },
  { value: "auth.password", label: "Passwords" },
  { value: "auth.session", label: "Sessions" },
  { value: "auth.email", label: "Email verification" },
  { value: "admin.", label: "Admin actions" },
  { value: "security.", label: "Rate limits" },
];

export default function HubAudit() {
  const [events, setEvents] = useState(null);
  const [type, setType] = useState("");
  const [outcome, setOutcome] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/audit", { params: { limit: 200, event_type: type || undefined, outcome: outcome || undefined } });
      setEvents(data);
    } catch (e) {
      toast.error(apiError(e).message);
      setEvents([]);
    }
  }, [type, outcome]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <ShellLayout area="hub" eyebrow="Network Owner" title="Security audit ledger">
      <div data-testid="hub-audit-page" className="space-y-6">
        <SectionHeading
          title="Append-only security events"
          description="Automatically written by the platform. Secrets, passwords and tokens are never recorded."
          action={
            <div className="flex flex-wrap gap-2">
              <select value={type} onChange={(e) => setType(e.target.value)} className="bz-input w-auto py-1.5 text-xs" data-testid="audit-filter-type">
                {TYPE_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
              <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="bz-input w-auto py-1.5 text-xs" data-testid="audit-filter-outcome">
                <option value="">Any outcome</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
              <button type="button" onClick={load} className="bz-btn-ghost text-xs" data-testid="audit-refresh-btn">
                <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} /> Refresh
              </button>
            </div>
          }
        />
        {events === null ? (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Spinner className="h-4 w-4 text-electric" /> Loading ledger…
          </div>
        ) : events.length === 0 ? (
          <EmptyState testId="audit-empty">No events match this filter.</EmptyState>
        ) : (
          <div className="bz-table-wrap">
            <table data-testid="audit-ledger-table" className="w-full">
              <thead>
                <tr>
                  <th className="bz-th">When</th>
                  <th className="bz-th">Event</th>
                  <th className="bz-th">Outcome</th>
                  <th className="bz-th">Actor</th>
                  <th className="bz-th">Target</th>
                  <th className="bz-th">IP</th>
                  <th className="bz-th">Details</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="bz-tr" data-testid={`audit-row-${e.id}`}>
                    <td className="bz-td whitespace-nowrap text-slate-400">{formatDateTime(e.created_at)}</td>
                    <td className="bz-td">
                      <span className="bz-code">{e.event_type}</span>
                    </td>
                    <td className="bz-td">
                      <OutcomeBadge outcome={e.outcome} />
                    </td>
                    <td className="bz-td font-mono text-xs text-slate-300">{e.actor_email || (e.actor_user_id ? shortId(e.actor_user_id) : "—")}</td>
                    <td className="bz-td font-mono text-xs text-slate-300">{e.target_email || (e.target_user_id ? shortId(e.target_user_id) : "—")}</td>
                    <td className="bz-td font-mono text-xs text-slate-400">{e.ip || "—"}</td>
                    <td className="bz-td max-w-xs truncate font-mono text-[11px] text-slate-500" title={JSON.stringify(e.metadata)}>
                      {Object.keys(e.metadata || {}).length ? JSON.stringify(e.metadata) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ShellLayout>
  );
}
